import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui';
import {
  acceptCookieConsent,
  hasCookieConsent,
} from '@/shared/lib/cookieConsent';
import { PDN_CONSENT_PDF, USER_AGREEMENT_PDF } from '@/shared/lib/legalDocs';

function DocLink({ href, children }: { href: string; children?: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary-700 underline decoration-primary-600/40 underline-offset-2 hover:text-primary-800"
    >
      {children}
    </a>
  );
}

/**
 * Site-wide banner: explicit consent to cookie / personal-data processing (ПнД).
 * Stays until the user accepts the current consent version.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!hasCookieConsent());
    const onConsent = () => setVisible(false);
    window.addEventListener('tsovetnik:cookie-consent', onConsent);
    return () => window.removeEventListener('tsovetnik:cookie-consent', onConsent);
  }, []);

  if (!visible) return null;

  const onAccept = () => {
    acceptCookieConsent();
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-border bg-surface/95 p-4 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-md"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-end sm:gap-6">
        <div className="min-w-0 flex-1 space-y-1.5">
          <p id="cookie-consent-title" className="text-sm font-semibold text-text-primary">
            {t('cookieConsent.title')}
          </p>
          <p id="cookie-consent-desc" className="text-sm leading-relaxed text-text-secondary">
            <Trans
              i18nKey="cookieConsent.body"
              components={{
                agreement: <DocLink href={USER_AGREEMENT_PDF} />,
                pdn: <DocLink href={PDN_CONSENT_PDF} />,
              }}
            />
          </p>
        </div>
        <Button
          type="button"
          onClick={onAccept}
          className="shrink-0 gradient-emerald-gold border-0 text-white shadow-emerald hover-scale sm:min-w-[11rem]"
        >
          {t('cookieConsent.accept')}
        </Button>
      </div>
    </div>
  );
}
